import type {
  ILayoutBlockPosterProps,
  ILayoutBlockTrailerProps,
} from 'blocks/LayoutBlock/Templates/LayoutBlockPoster.h'

import { styled } from '@linaria/react'
import {
  OverlayLayout as OverlayLayoutBanner,
  PosterContainer as PosterContainerBanner,
} from 'blocks/Banner/Banner.styled'
import { ContentStateBadgeSC } from 'blocks/ContentStateBadge/ContentStateBadge.styled'
import { PosterOverlay } from 'blocks/LayoutBlock/Templates/LayoutBlockPoster.h'
import { BANNER_RESERVED_HEIGHT_PROPERTY } from 'blocks/PosterContent'
import { BANNER_ASPECT_RATIO } from 'vars/banner-aspect-ratio'
import { COLOR } from 'vars/color'
import { FIXED_INDENTS } from 'vars/fixed-indents'
import { MEDIA } from 'vars/media'
import { LayoutBlockPlayerSC } from './LayoutBlockPlayer.styles'

export const OverlayLayout = styled.div<
  Pick<
    ILayoutBlockPosterProps,
    | 'overlayEnabled'
    | 'overlay'
    | 'overlayDirection'
    | 'overlayHeight'
    | 'overlayWidth'
    | 'overlayVertical'
    | 'overlayHorizontal'
  >
>`
  display: ${({ overlayEnabled }) => (overlayEnabled ? 'block' : 'none')};
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background: ${({ overlay, overlayDirection }) =>
    overlay &&
    (overlayDirection === 'OVERLAY_FULL' ||
      overlayDirection === 'OVERLAY_HORIZONTAL_FULL' ||
      overlayDirection === 'OVERLAY_HORIZONTAL_VERTICAL_FULL' ||
      overlayDirection === 'OVERLAY_VERTICAL_FULL')
      ? `var(${overlay})`
      : PosterOverlay.None};

  &:before {
    display: ${({ overlayDirection }) =>
      overlayDirection === 'OVERLAY_VERTICAL' ||
      overlayDirection === 'OVERLAY_HORIZONTAL_VERTICAL' ||
      overlayDirection === 'OVERLAY_VERTICAL_FULL' ||
      overlayDirection === 'OVERLAY_HORIZONTAL_VERTICAL_FULL'
        ? 'block'
        : 'none'};
    content: '';
    width: 100%;
    height: ${({ overlayHeight }) => `${overlayHeight || 0}%`};
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    background: ${({ overlayVertical }) => `var(${overlayVertical})`};
  }

  &:after {
    display: ${({ overlayDirection }) =>
      overlayDirection === 'OVERLAY_HORIZONTAL' ||
      overlayDirection === 'OVERLAY_HORIZONTAL_FULL' ||
      overlayDirection === 'OVERLAY_HORIZONTAL_VERTICAL_FULL' ||
      overlayDirection === 'OVERLAY_HORIZONTAL_VERTICAL'
        ? 'block'
        : 'none'};
    content: '';
    width: ${({ overlayWidth }) => `${overlayWidth || 0}%`};
    transform: translateX(-1px);
    height: 100%;
    position: absolute;
    background: ${({ overlayHorizontal }) => `var(${overlayHorizontal})`};
  }
`

export const LayoutBlockPosterSC = styled.div<
  Omit<ILayoutBlockPosterProps, 'posterUrl' | 'posterMobileUrl'>
>`
  background: ${({ backgroundColor }) =>
    backgroundColor
      ? `var(${backgroundColor})`
      : `var(${COLOR.TEXT_ICON_CONSTANT_DARK})`};
  width: ${({ width }) => (width ? `var(${width})` : `0`)};
  max-width: 100%;
  aspect-ratio: ${({ aspectRatio }) =>
    aspectRatio ? `var(${aspectRatio})` : `0`};
  position: absolute;
  top: var(--navbar-height-default);
  left: 0;
  padding: ${({ paddings }) =>
    paddings?.length
      ? paddings.map((padding) => `var(${padding})`).join(' ')
      : `0`};
  /* Solution for browser versions, where the 'aspect-ratio' property is not supported */
  @supports not (
    aspect-ratio: ${({ aspectRatio }) =>
        aspectRatio ? `var(${aspectRatio})` : `0`}
  ) {
    &::before {
      float: left;
      padding-top: ${({ aspectRatio }) => `calc(100% / var(${aspectRatio}))`};
      content: '';
    }

    &::after {
      display: block;
      content: '';
      clear: both;
    }
  }

  ${LayoutBlockPlayerSC} {
    aspect-ratio: var(${BANNER_ASPECT_RATIO.SIZE_50});
  }
`

export const LayoutBlockTrailerSC = styled(LayoutBlockPosterSC)<
  Pick<ILayoutBlockTrailerProps, 'visible'>
>`
  opacity: ${({ visible }) => (visible ? 1 : 0)};
`

/**
 * `.banner base` from the content details concept. The shell is wider than
 * 16:9 from 1240 up; artwork and trailer fill it like home banners while the
 * details horizontal scrim carries the banner-description over the dark side.
 */
export const DetailsBannerPosterSC = styled(LayoutBlockPosterSC)`
  overflow: hidden;

  /* PosterContent reserves the banner's place in the page and grows past the
     aspect ratio when a long title needs the room, so follow it rather than
     the ratio and keep the artwork reaching the details section. */
  height: var(${BANNER_RESERVED_HEIGHT_PROPERTY}, auto);

  /*
   * From 600 to 1239 the compact floor (DETAILS_BANNER_COMPACT) opens a dark
   * band under the 16:9 artwork for banner-description, so pin the artwork
   * to 16:9 at the top and confine its bottom fade to that same band —
   * spanning the untouched band below with the fade too stops it short of
   * full black, leaving a visible seam where the artwork's un-faded edge
   * meets the flat background. Below 600 there is no floor: the artwork
   * already fills the shell, banner-description overlays it directly, and
   * the default full-height fade already ends flush with its bottom edge.
   */
  &:not([data-details-trailer-playing='true']) {
    ${MEDIA.MAX_1239} {
      ${MEDIA.MIN_600} {
        ${PosterContainerBanner} {
          width: 100%;
          height: auto;
          aspect-ratio: var(${BANNER_ASPECT_RATIO.SIZE_50});
          top: 0;
        }

        ${OverlayLayoutBanner} {
          &:before {
            top: 0;
            bottom: auto;
            height: auto;
            aspect-ratio: var(${BANNER_ASPECT_RATIO.SIZE_50});
          }
        }
      }
    }
  }

  ${OverlayLayoutBanner} {
    /* Keeps the scrim order below local, so it cannot paint over the
       banner-description that follows the banner in the page flow. */
    isolation: isolate;

    &:before {
      /* The shell-wide bottom fade sits above the artwork scrim. */
      z-index: 1;
      background: var(${COLOR.BANNER_DETAILS_VERTICAL_OVERLAY});
    }

    ${MEDIA.MIN_1240} {
      &:after {
        display: block;
        top: 0;
        right: 0;
        left: 0;
        width: 100%;
        height: 100%;
        aspect-ratio: initial;
        transform: none;
        background: var(${COLOR.BANNER_DETAILS_HORIZONTAL_OVERLAY});
      }
    }
  }

  /*
   * Compact details (600–1023) raise a 580px floor under the 16:9 artwork,
   * so percentage top would centre the badge on the tall shell and drop it
   * onto the copy. Percentage margins resolve against width, so
   * margin-top: 50% / 16:9-ratio is the artwork's vertical centre, and
   * Figma keeps the badge in that centre. Below 600 the artwork already
   * fills the portrait shell, so ContentStateBadge keeps its own top-edge
   * rule (INDENT_200). From 1024 the compact floor is 0 and the shell is
   * 16:9, so Figma moves the badge to the free right-hand side
   * (INDENT_300 / 120px, then 200px from 1240). Home banners share that
   * compact-centre then right-side ladder on ContentStateBadge itself.
   */
  ${ContentStateBadgeSC} {
    ${MEDIA.MIN_600} {
      top: 0;
      right: auto;
      left: 50%;
      margin-top: calc(50% / var(${BANNER_ASPECT_RATIO.SIZE_50}));
      transform: translate(-50%, -50%);
    }

    ${MEDIA.MIN_1024} {
      right: var(${FIXED_INDENTS.INDENT_300});
      left: auto;
      transform: translateY(-50%);
    }

    ${MEDIA.MIN_1240} {
      top: 50%;
      right: calc(
        var(${FIXED_INDENTS.INDENT_400}) + var(${FIXED_INDENTS.INDENT_100})
      );
      left: auto;
      margin-top: 0;
      transform: translateY(-50%);
    }
  }
`

export const DetailsBannerTrailerSC = styled(DetailsBannerPosterSC)<
  Pick<ILayoutBlockTrailerProps, 'visible'>
>`
  opacity: ${({ visible }) => (visible ? 1 : 0)};

  /*
   * From 1240 the details shell is wider than 16:9. Home banners fill that
   * extra width; the inherited LayoutBlockPlayerSC 16:9 box would sit on the
   * left and leave a dark band on the right. Drop the ratio and pin the
   * player to the shell so the trailer stretches across the full width.
   */
  ${MEDIA.MIN_1240} {
    ${LayoutBlockPlayerSC} {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      max-width: none;
      aspect-ratio: unset;
    }
  }
`
